/**
 * Product type and display utilities for consistent cart/order display
 */

export type ProductType = 'template' | 'service' | 'kit' | 'bundle' | 'addon';

export interface ProductDisplayInfo {
  type: ProductType;
  displayName: string;
  typeLabel: string;
  maxQuantity: number;
}

// Known add-on slug mappings to customer-friendly display names
const ADDON_DISPLAY_NAMES: Record<string, string> = {
  'extra-dispute': 'Extra Dispute Add-on',
  'extra_dispute': 'Extra Dispute Add-on',
  'expedite': 'Expedite Processing Add-on',
  'express': 'Express Processing Add-on',
  'rush': 'Rush Processing Add-on',
  'additional-round': 'Additional Dispute Round Add-on',
  'additional_round': 'Additional Dispute Round Add-on',
  'packet-review-addon-express': 'Express 24-Hour Turnaround',
  'packet-review-addon-round2': 'Additional Review Round',
};

// Slugs that indicate packet review services
const PACKET_REVIEW_SERVICE_SLUGS = [
  'packet-review-basic',
  'packet-review-plus', 
  'packet-review-premium',
];

// Slugs that indicate add-ons (not standalone services)
const ADDON_SLUG_PATTERNS = ['addon', 'express', 'expedite', 'rush', 'extra'];

// Slugs that should be labeled as "Bundle" (Complete DIY Credit Dispute Template Package)
const BUNDLE_SLUGS = ['complete-package', 'complete-bundle', 'full-bundle', 'all-templates'];

// Slugs for kits (different from bundles - more specific product collections)
const KIT_SLUGS = ['complete-kit', 'diy-kit', 'starter-kit', 'credit-scrub-kit'];

/**
 * Determines the product type based on slug and product properties
 */
export function getProductType(slug: string | null | undefined, isBundle: boolean): ProductType {
  const normalizedSlug = (slug || '').toLowerCase();
  
  // Check if it's explicitly a bundle by slug (highest priority)
  if (BUNDLE_SLUGS.some(bundleSlug => normalizedSlug.includes(bundleSlug))) {
    return 'bundle';
  }
  
  // Check if it's a bundle by is_bundle flag
  if (isBundle) {
    // Check if it should be labeled as a kit
    if (KIT_SLUGS.some(kitSlug => normalizedSlug.includes(kitSlug))) {
      return 'kit';
    }
    return 'bundle';
  }
  
  // Check if it's a packet review service (not addon)
  if (PACKET_REVIEW_SERVICE_SLUGS.includes(normalizedSlug)) {
    return 'service';
  }
  
  // Check if it's an addon
  if (ADDON_SLUG_PATTERNS.some(pattern => normalizedSlug.includes(pattern))) {
    return 'addon';
  }
  
  // Check for service-related slugs
  if (normalizedSlug.includes('review') || normalizedSlug.includes('service')) {
    return 'service';
  }
  
  // Default to template for everything else
  return 'template';
}

/**
 * Gets the customer-friendly type label for display badges
 */
export function getTypeLabel(type: ProductType): string {
  switch (type) {
    case 'template':
      return 'Template';
    case 'service':
      return 'Service';
    case 'kit':
      return 'Complete Kit';
    case 'bundle':
      return 'Bundle';
    case 'addon':
      return 'Add-on';
    default:
      return 'Item';
  }
}

/**
 * Gets the max quantity allowed for a product type
 */
export function getMaxQuantity(type: ProductType, slug: string | null | undefined): number {
  // All templates are limited to 1
  if (type === 'template') {
    return 1;
  }
  
  // Bundles and kits are limited to 1
  if (type === 'bundle' || type === 'kit') {
    return 1;
  }
  
  // Services are typically limited to 1 (can only buy one packet review tier)
  if (type === 'service') {
    return 1;
  }
  
  // Add-ons default to 1 unless we have specific rules
  return 1;
}

/**
 * Generates a readable display name from a slug
 */
function slugToDisplayName(slug: string): string {
  return slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
    .trim();
}

/**
 * Gets the customer-friendly display name for an add-on
 */
function getAddonDisplayName(slug: string | null | undefined, productName: string): string {
  const normalizedSlug = (slug || '').toLowerCase();
  
  // Check known add-on mappings
  if (ADDON_DISPLAY_NAMES[normalizedSlug]) {
    return ADDON_DISPLAY_NAMES[normalizedSlug];
  }
  
  // Check partial matches in slug
  for (const [pattern, displayName] of Object.entries(ADDON_DISPLAY_NAMES)) {
    if (normalizedSlug.includes(pattern.replace(/[-_]/g, ''))) {
      return displayName;
    }
  }
  
  // Fallback to product name if it exists
  if (productName && productName.trim()) {
    return productName;
  }
  
  // Last resort: generate from slug
  if (slug) {
    return slugToDisplayName(slug);
  }
  
  return 'Add-on';
}

/**
 * Gets complete display info for a product
 */
export function getProductDisplayInfo(
  slug: string | null | undefined,
  isBundle: boolean,
  productName: string
): ProductDisplayInfo {
  const type = getProductType(slug, isBundle);
  const typeLabel = getTypeLabel(type);
  const maxQuantity = getMaxQuantity(type, slug);
  
  let displayName = productName;
  
  // For add-ons, use our mapping to get customer-friendly names
  if (type === 'addon') {
    displayName = getAddonDisplayName(slug, productName);
  }
  
  return {
    type,
    displayName,
    typeLabel,
    maxQuantity,
  };
}

/**
 * Generates an order summary label based on cart contents
 * Returns a customer-friendly summary like "2 Templates, 1 Service"
 */
export function getOrderSummaryLabel(items: Array<{ slug?: string | null; isBundle: boolean }>): string {
  const typeCounts: Record<ProductType, number> = {
    template: 0,
    service: 0,
    kit: 0,
    bundle: 0,
    addon: 0,
  };
  
  items.forEach(item => {
    const type = getProductType(item.slug, item.isBundle);
    typeCounts[type]++;
  });
  
  const parts: string[] = [];
  
  if (typeCounts.template > 0) {
    parts.push(`${typeCounts.template} Template${typeCounts.template > 1 ? 's' : ''}`);
  }
  if (typeCounts.service > 0) {
    parts.push(`${typeCounts.service} Service${typeCounts.service > 1 ? 's' : ''}`);
  }
  if (typeCounts.kit > 0) {
    parts.push(`${typeCounts.kit} Kit${typeCounts.kit > 1 ? 's' : ''}`);
  }
  if (typeCounts.bundle > 0) {
    parts.push(`${typeCounts.bundle} Bundle${typeCounts.bundle > 1 ? 's' : ''}`);
  }
  if (typeCounts.addon > 0) {
    parts.push(`${typeCounts.addon} Add-on${typeCounts.addon > 1 ? 's' : ''}`);
  }
  
  if (parts.length === 0) {
    return '0 Items';
  }
  
  return parts.join(', ');
}
